/**
 * Telling "you asked for an impossible window" apart from "the page is broken".
 *
 * The endpoint answers a bad window with a **400** whose body is
 * `{"error":{"message","context"}}`, and its messages are deliberately written
 * to be shown to a user — "The requested time window is too long. The maximum
 * is 365 days; this request covers N days." So a 400 is routed to the filter
 * bar as inline text, and everything else (403, 5xx, transport) is a genuine
 * failure that belongs to the error boundary.
 *
 * Nothing narrower than the status code is inspected. The three rejections the
 * endpoint can produce — inverted range, unparseable timestamp, over-long span
 * — are all 400s carrying a sentence worth showing, and a fourth added later
 * would be too; matching on message text would silently stop recognising it.
 */
import { getErrorMessage, isApiError } from '@/shared/api/utils/error'

import type { ApiError } from '@/shared/api/utils/error'

/** A 400 from the overview endpoint: the caller's window, not the page. */
export function isWindowRejection(error: unknown): error is ApiError {
	return isApiError(error) && error.status === 400
}

/**
 * The sentence to put under the filter bar.
 *
 * The api client already unwraps `{"error":{"message"}}` into the thrown
 * error's own message, so this is that message — falling back to a generic line
 * only if a future rejection ever arrives without one.
 */
export function windowRejectionMessage(error: ApiError): string {
	const message = getErrorMessage(error)
	return message.trim().length > 0
		? message
		: 'That time range cannot be used. Pick a different one.'
}
